import React, { useState } from 'react';
import { useUser } from 'util/UserContext';
import api from 'config/axiosConfig';
import { handleLogout } from 'components/Auth/AuthLogout';
import Modal from 'ui/Modal';
import InputField from 'ui/InputField';
import InputTextArea from 'ui/InputTextArea';

interface Props {
    isOpen: boolean;
    onClose: () => void;
}

const UserSettingsAccountDelete: React.FC<Props> = ({ isOpen, onClose }) => {
    const { currentUser } = useUser();
    const [email, setEmail] = useState<string>('');
    const [reason, setReason] = useState<string>('');
    const [errorMessage, setErrorMessage] = useState<string>('');
    const [isEmailValid, setIsEmailValid] = useState<boolean>(false);
    const [showConfirmation, setShowConfirmation] = useState(false);

    const handleEmailConfirm = (e: React.ChangeEvent<HTMLInputElement>) => {
        const emailValue = e.target.value;
        setEmail(emailValue);
        setIsEmailValid(/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailValue));
        setErrorMessage('');
    };

    const handleDelete = async () => {
        if (!currentUser) return;
        if (!isEmailValid || email !== currentUser.email) {
            setErrorMessage('The provided email is not associated with this account.');
            return;
        }
        try {
            await api.delete('/api/user/delete', {
                data: { username: currentUser.username, reason },
            });
            handleLogout({ redirectToHome: true });
        } catch (error) {
            console.error('Error deleting account:', error);
            setErrorMessage('Failed to delete account. Please try again later.');
        }
    };

    const handleSubmit = () => {
        if (!isEmailValid || email !== currentUser?.email) {
            setErrorMessage('The provided email is not associated with this account.');
            return;
        }
        setShowConfirmation(true);
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title="Delete account"
            confirmationText="This action is irreversible. Are you sure you want to delete your account?"
            onConfirm={handleDelete}
            showConfirmation={showConfirmation}
            onCancelConfirmation={() => setShowConfirmation(false)}
        >
            <div className="flex flex-col h-[calc(100%-44px)] max-h-[calc(100%-44px)] sm:h-auto overflow-y-scroll sm:overflow-y-auto space-y-4">
                <div className="flex flex-col flex-grow space-y-4">
                    <p className="text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">
                        We're sad that you're leaving but hope you'll keep fighting the good fight.
                    </p>

                    <form className="flex flex-col flex-grow space-y-4">
                        <div className="form-control mb-0">
                            <InputField
                                type="email"
                                id="email"
                                name="email"
                                labelText="Email"
                                autoComplete="email"
                                fieldRequired
                                value={email}
                                spellCheck={false}
                                onChange={handleEmailConfirm}
                                error={errorMessage}
                            />
                        </div>

                        <div className="form-control">
                            <InputTextArea
                                id="reason"
                                name="reason"
                                labelText="Reason"
                                fieldPlaceholder="Tell us why you're leaving"
                                value={reason}
                                maxLength={2000}
                                onChange={(e) => setReason(e.target.value)}
                            />
                        </div>
                    </form>
                </div>

                {/* Action Buttons */}
                <div className="flex flex-grow flex-col pb-8 justify-end place-items-end mb-6 mt-auto space-y-2 sm:space-y-0 sm:space-x-2 sm:flex sm:justify-end sm:flex-row sm:mb-0">
                    <button
                        type="button"
                        onClick={onClose}
                        className="btn-secondary w-full sm:w-fit py-2 px-4"
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        onClick={handleSubmit}
                        disabled={!isEmailValid}
                        className={`w-full sm:w-fit py-2 px-4 ${
                            !isEmailValid ? 'btn-primary-disabled' : 'btn-primary'
                        }`}
                    >
                        Submit
                    </button>
                </div>
            </div>
        </Modal>
    );
};

export default UserSettingsAccountDelete; 